// src/WorkspaceInviteDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import api from './apiClient'; // Use centralized client
import { useSnackbar } from 'notistack';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

function WorkspaceInviteDialog({
  open,
  onClose,
  workspaceId,
  workspaceName,
  currentUserId,
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleSend = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      enqueueSnackbar('Please enter an email', { variant: 'warning' });
      return;
    }
    if (!trimmedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      enqueueSnackbar('Invalid email format', { variant: 'warning' });
      return;
    }
    if (!workspaceId) {
      enqueueSnackbar('No workspace selected', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      await api.post('/api/workspace-invitations', {
        workspace_id: workspaceId,
        email: trimmedEmail,
        role,
        invited_by: currentUserId,
      });
      enqueueSnackbar('Invitation sent successfully!', { variant: 'success' });
      setEmail('');
      setRole('viewer');
      onClose();
    } catch (err) {
      console.error('Error sending invite:', err);
      enqueueSnackbar(
        err.response?.data?.error || 'Error sending invite',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Invite to {workspaceName || 'workspace'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the email of the user you want to add. They will need to sign
          up with this email and accept the invitation.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Email"
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoFocus
          />

          <FormControl size="small">
            <InputLabel id="invite-role-label">Role</InputLabel>
            <Select
              labelId="invite-role-label"
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={loading}
            >
              {ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={loading || !email.trim()}
        >
          {loading ? <CircularProgress size={20} /> : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WorkspaceInviteDialog;